import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDashboardManifest } from '../../../../api/insights'
import { useDashboardUrlState, type UseDashboardUrlStateResult } from '../../filter/useDashboardUrlState'
import { toDashboardFilter } from '../../filter/dashboardFilterReducer'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import type { DashboardFilter } from '../../../../types/insights'

export interface UseOperationsAnalyticsResult extends UseDashboardUrlStateResult {
  filter: DashboardFilter
  widgetProps: (widgetKey: string) => WidgetProps
  filterFieldsOpen: boolean
  setFilterFieldsOpen: (open: boolean) => void
  /**
   * The manifest's capability render-gate for the two 4.2 manifest-gate-only tiles (design §3.1) —
   * `null` while the manifest fetch is in flight (both tiles show `loading`, never a premature
   * `not_authorized` flash). `true`/`false` once resolved.
   */
  ingestionRunVolumeAvailable: boolean | null
  aiAcceptRateAvailable: boolean | null
  /** A stable key both 4.2 tiles re-fetch on when it changes (mirrors `useWidgetData`'s `filterKey`). */
  refreshKey: string
}

/**
 * IB-282 View 4 — thin composition hook, mirrors `useJurisdictionAnalytics`/`usePortfolioAnalytics`'s
 * own shape. The ONE addition over those siblings: a manifest fetch to resolve the two 4.2 tiles'
 * capability render-gate (design §3.1) — `case-aging-by-status` needs no such fetch, it is a real
 * `useWidgetData` endpoint that resolves its own `not_authorized` state independently.
 */
export function useOperationsAnalytics(): UseOperationsAnalyticsResult {
  const navigate = useNavigate()
  const urlState = useDashboardUrlState()
  const [filterFieldsOpen, setFilterFieldsOpen] = useState(false)
  const [ingestionRunVolumeAvailable, setIngestionRunVolumeAvailable] = useState<boolean | null>(null)
  const [aiAcceptRateAvailable, setAiAcceptRateAvailable] = useState<boolean | null>(null)

  const filter: DashboardFilter = toDashboardFilter(urlState.state)
  const refreshKey = useMemo(() => JSON.stringify(filter), [filter])

  useEffect(() => {
    const controller = new AbortController()
    setIngestionRunVolumeAvailable(null)
    setAiAcceptRateAvailable(null)
    getDashboardManifest(filter, controller.signal)
      .then((manifest) => {
        if (controller.signal.aborted) return
        const byKey = new Map(manifest.widgets.map((w) => [w.key, w.available]))
        setIngestionRunVolumeAvailable(byKey.get(WIDGET_KEY.ingestionRunVolume) ?? false)
        setAiAcceptRateAvailable(byKey.get(WIDGET_KEY.aiAcceptRate) ?? false)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        // A manifest-fetch failure degrades to "not authorized" for both — never a thrown/blank
        // tile; the sibling case-aging-by-status tile is unaffected (it does not depend on this
        // fetch at all).
        setIngestionRunVolumeAvailable(false)
        setAiAcceptRateAvailable(false)
      })
    return () => controller.abort()
  }, [refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  function onDrill(rsql: string): void {
    navigate(`/cases/filings${rsql ? `?filter=${encodeURIComponent(rsql)}` : ''}`)
  }

  function widgetProps(widgetKey: string): WidgetProps {
    return {
      filter,
      skipRefetch: urlState.state.crossFilterOrigin === widgetKey,
      onCrossFilter: (axis, value) =>
        urlState.dispatch({ type: 'ADD_CROSSFILTER', axis, value, sourceWidget: widgetKey }),
      onDrill,
    }
  }

  return {
    ...urlState,
    filter,
    widgetProps,
    filterFieldsOpen,
    setFilterFieldsOpen,
    ingestionRunVolumeAvailable,
    aiAcceptRateAvailable,
    refreshKey,
  }
}
